/**
 * feedback-images.ts — Ảnh chụp màn hình đính kèm góp ý: thu nhỏ về JPEG trước khi gửi (ảnh chụp màn hình Retina có thể vài MB).
 * `fitSize` là hàm thuần (có test); phần vẽ canvas chỉ chạy trong trình duyệt.
 */
export const MAX_IMAGES = 3
export const MAX_SIDE = 1600
export const JPEG_QUALITY = 0.82

/** Thu nhỏ giữ tỉ lệ sao cho cạnh dài ≤ max; ảnh nhỏ hơn thì giữ nguyên (không phóng to). */
export function fitSize(w: number, h: number, max = MAX_SIDE): { w: number; h: number } {
  const longest = Math.max(w, h)
  if (longest <= max || longest <= 0) return { w, h }
  const k = max / longest
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) }
}

/** File ảnh (hoặc ảnh dán từ clipboard) → data URL JPEG đã thu nhỏ. Lỗi nếu trình duyệt không đọc được ảnh. */
export async function shrinkImage(file: Blob): Promise<string> {
  const bmp = await createImageBitmap(file)
  try {
    const { w, h } = fitSize(bmp.width, bmp.height)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Trình duyệt không xử lý được ảnh') // i18n-ignore — lỗi nội bộ, không hiện cho người dùng (nơi gọi tự thay bằng câu đã dịch)
    // Nền trắng: ảnh PNG trong suốt chuyển sang JPEG sẽ không bị đen.
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(bmp, 0, 0, w, h)
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY)
  } finally {
    bmp.close()
  }
}

/** Lấy các ảnh từ sự kiện dán (Ctrl/⌘+V ảnh chụp màn hình). */
export function imagesFromClipboard(data: DataTransfer | null): File[] {
  if (!data) return []
  return [...data.items].filter((it) => it.kind === 'file' && it.type.startsWith('image/')).flatMap((it) => {
    const f = it.getAsFile()
    return f ? [f] : []
  })
}
